import React, {useMemo} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendarDays} from "@fortawesome/free-solid-svg-icons";
import Select, {SingleValue} from 'react-select';
import './map-mode-control.scss';
import type {MapModeType, MapSpeciesOption} from "../views/map/types";
import {MapMode} from "../views/map/constants";

export type MapModeControlProps = {
    mode: MapModeType,
    tourMode?: boolean,
    activeSpecies?: number,
    periodLabel: string,
    speciesOptions: MapSpeciesOption[],
    onSelectSpecies: (speciesCode: string) => void,
    onSetMapExplorerMode: (nextMode: MapModeType, nextTourMode?: boolean) => void,
    onShowTimePeriodSelector: () => void
}

export function MapModeControl({
    mode,
    tourMode = false,
    activeSpecies,
    periodLabel,
    speciesOptions,
    onSelectSpecies,
    onSetMapExplorerMode,
    onShowTimePeriodSelector
}: MapModeControlProps) {
    const isYearExplorerMode = mode === MapMode.Year && !tourMode;
    const isSpeciesExplorerMode = mode === MapMode.Species;
    const isHotspotExplorerMode = mode === MapMode.Hotspot;
    const isTourMode = mode === MapMode.Year && tourMode;
    const isSpeciesMode = mode === MapMode.Species;
    const selectedSpeciesOption = useMemo(() => {
        return speciesOptions.find(species => species.taxonomicOrder === activeSpecies);
    }, [activeSpecies, speciesOptions]);
    const selectOptions = useMemo(() => {
        return speciesOptions.map(species => ({
            value: species.speciesCode,
            label: species.commonName + (species.scientificName ? ` - ${species.scientificName}` : '')
        }));
    }, [speciesOptions]);
    const selectedSelectOption = useMemo(() => {
        if (!selectedSpeciesOption) {
            return null;
        }

        return {
            value: selectedSpeciesOption.speciesCode,
            label: selectedSpeciesOption.commonName + (selectedSpeciesOption.scientificName ? ` - ${selectedSpeciesOption.scientificName}` : '')
        };
    }, [selectedSpeciesOption]);

    return (
        <div className="map-mode-control">
            {isSpeciesMode ?
                <div className="map-mode-control__species-row">
                    <div className="map-mode-control__heading">Exploring Species</div>
                    <div className="map-mode-control__species-select">
                        <Select
                            classNamePrefix="map-mode-control-species"
                            className="map-mode-control-species"
                            isSearchable
                            isClearable={false}
                            value={selectedSelectOption}
                            options={selectOptions}
                            aria-label="Select a species"
                            placeholder="Select a species"
                            onChange={(option: SingleValue<{ value: string; label: string }>) => {
                                if (option) {
                                    onSelectSpecies(option.value);
                                }
                            }}
                        />
                    </div>
                </div>
                :
                <div className="map-mode-control__period">
                    <div className="map-mode-control__heading">Exploring {periodLabel}</div>
                    <button type="button" className="map-mode-control__period-button" onClick={onShowTimePeriodSelector}>
                        <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                        <span>Change Dates</span>
                    </button>
                </div>
            }
            <div className="map-mode-control__toggle" role="group" aria-label="Year map mode">
                <button
                    type="button"
                    className={`map-mode-control__toggle-button ${isYearExplorerMode ? 'is-active' : ''}`}
                    aria-pressed={isYearExplorerMode}
                    onClick={() => onSetMapExplorerMode(MapMode.Year)}
                >
                    Year Explorer
                </button>
                <button
                    type="button"
                    className={`map-mode-control__toggle-button ${isSpeciesExplorerMode ? 'is-active' : ''}`}
                    aria-pressed={isSpeciesExplorerMode}
                    onClick={() => onSetMapExplorerMode(MapMode.Species)}
                >
                    Species Explorer
                </button>
                <button
                    type="button"
                    className={`map-mode-control__toggle-button ${isHotspotExplorerMode ? 'is-active' : ''}`}
                    aria-pressed={isHotspotExplorerMode}
                    onClick={() => onSetMapExplorerMode(MapMode.Hotspot)}
                >
                    Hotspot Explorer
                </button>
                <button
                    type="button"
                    className={`map-mode-control__toggle-button ${isTourMode ? 'is-active' : ''}`}
                    aria-pressed={isTourMode}
                    onClick={() => onSetMapExplorerMode(MapMode.Year, true)}
                >
                    Tour Mode
                </button>
            </div>
        </div>
    )
}
